/**
 * Schedules for automations: one moment or several, every N seconds, clock times on chosen days,
 * the US market's own open / close, or a cron line — from seconds to years, in any IANA time zone,
 * with the NYSE calendar (holidays and early closes) built in.
 *
 * Pure (no server imports): the scheduler, the AI tools and the client's live preview all compute
 * the same next runs from the same code. Every run time is a whole second.
 */
import { z } from 'zod'

export const DEFAULT_TZ = 'America/New_York'
const ET = 'America/New_York'
const DAY_MS = 86_400_000

/* ── text forms ─────────────────────────────────────────────────────────── */

const CLOCK_RE = /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/
const LOCAL_RE = /^(\d{4})-(\d{2})-(\d{2})[ T]([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/
const MONTH_DAY_RE = /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/

const Clock = z.string().trim().regex(CLOCK_RE, 'a time is "HH:MM" or "HH:MM:SS" (24-hour)')
const LocalDateTime = z.string().trim().regex(LOCAL_RE, 'a moment is "YYYY-MM-DD HH:MM" or "YYYY-MM-DD HH:MM:SS"')
const Weekday = z.number().int().min(0).max(6)

export const SCHEDULE_KINDS = ['once', 'interval', 'daily', 'weekly', 'monthly', 'yearly', 'market', 'cron'] as const
export type ScheduleKind = (typeof SCHEDULE_KINDS)[number]
export type DayFilter = 'all' | 'weekdays' | 'trading'
export type MarketEvent = 'premarket' | 'open' | 'close' | 'afterhours'
export type ScheduleWindow = 'session' | 'extended' | { from: string; to: string }

/**
 * When something runs. `kind` picks the pattern; the rest narrows it:
 *  - once      `at`: one or more local moments ("2026-09-29 09:25:30").
 *  - interval  `every` seconds, from `start` (else from creation); with a `window` the runs line up
 *              with the window's start each day (every 15 min from 09:30).
 *  - daily     `times` every day (one or many, seconds allowed).
 *  - weekly    `weekdays` (0 = Sun … 6 = Sat) at `times`.
 *  - monthly   `monthDays` (1–31, -1 = the last day; a day a month lacks = its last day) or `nth`
 *              (the 1st…5th / last (-1) weekday), optionally only in `months`, at `times`.
 *  - yearly    `dates` ("MM-DD"; Feb 29 falls on Feb 28 in other years) at `times`.
 *  - market    each NYSE session's `event` (premarket 04:00, open 09:30, close 16:00 / 13:00 on
 *              early-close days, afterhours end 20:00 / 17:00) ± `offsetMin`, US Eastern.
 *  - cron      "sec min hour day month weekday" (or the classic 5 fields without seconds).
 * `days` keeps only weekdays or NYSE trading days (a monthly / yearly date that misses moves to
 * the next allowed day, the last-day ones to the previous); `window` limits intervals and cron
 * to part of the day ("session" = the NYSE regular session); `start` / `end` bound it all, and
 * the job stops after `maxRuns`.
 */
export const ScheduleSchema = z
  .object({
    kind: z.enum(SCHEDULE_KINDS),
    at: z.array(LocalDateTime).min(1).max(100).optional(),
    every: z.number().int().min(1).max(366 * 86_400).optional(),
    times: z.array(Clock).min(1).max(96).optional(),
    weekdays: z.array(Weekday).min(1).max(7).optional(),
    monthDays: z.array(z.number().int().min(-1).max(31)).min(1).max(31).optional(),
    nth: z.object({ n: z.number().int().min(-1).max(5), weekday: Weekday }).optional(),
    months: z.array(z.number().int().min(1).max(12)).min(1).max(12).optional(),
    dates: z.array(z.string().trim().regex(MONTH_DAY_RE, 'a yearly date is "MM-DD"')).min(1).max(60).optional(),
    event: z.enum(['premarket', 'open', 'close', 'afterhours']).optional(),
    offsetMin: z.number().int().min(-720).max(720).optional(),
    cron: z.string().trim().max(200).optional(),
    tz: z.string().trim().min(1).max(64).default(DEFAULT_TZ),
    days: z.enum(['all', 'weekdays', 'trading']).default('all'),
    window: z.union([z.enum(['session', 'extended']), z.object({ from: Clock, to: Clock })]).optional(),
    start: LocalDateTime.optional(),
    end: LocalDateTime.optional(),
    maxRuns: z.number().int().min(1).max(1_000_000).optional(),
  })
  .superRefine((s, ctx) => {
    const need = (field: keyof typeof s, what: string) => {
      if (s[field] == null) ctx.addIssue({ code: 'custom', path: [field], message: `${s.kind} needs ${what}` })
    }
    switch (s.kind) {
      case 'once':
        need('at', 'at: one or more moments "YYYY-MM-DD HH:MM[:SS]"')
        break
      case 'interval':
        need('every', 'every: the gap in seconds')
        break
      case 'daily':
        need('times', 'times: ["HH:MM", …]')
        break
      case 'weekly':
        need('weekdays', 'weekdays: [0 = Sun … 6 = Sat]')
        need('times', 'times: ["HH:MM", …]')
        break
      case 'monthly':
        need('times', 'times: ["HH:MM", …]')
        if (s.monthDays == null && s.nth == null)
          ctx.addIssue({ code: 'custom', path: ['monthDays'], message: 'monthly needs monthDays (1–31, -1 = last day) or nth ({n, weekday})' })
        if (s.monthDays?.includes(0)) ctx.addIssue({ code: 'custom', path: ['monthDays'], message: 'monthDays: 1–31, or -1 for the last day' })
        if (s.nth?.n === 0) ctx.addIssue({ code: 'custom', path: ['nth'], message: 'nth.n: 1–5, or -1 for the last' })
        break
      case 'yearly':
        need('dates', 'dates: ["MM-DD", …]')
        need('times', 'times: ["HH:MM", …]')
        break
      case 'market':
        need('event', 'event: premarket | open | close | afterhours')
        break
      case 'cron': {
        need('cron', 'cron: "sec min hour day month weekday"')
        const c = s.cron != null ? parseCron(s.cron) : null
        if (typeof c === 'string') ctx.addIssue({ code: 'custom', path: ['cron'], message: c })
        break
      }
    }
    if (!validTz(s.tz)) ctx.addIssue({ code: 'custom', path: ['tz'], message: `unknown time zone "${s.tz}" (use an IANA name like America/New_York or America/Chicago)` })
    if (s.window && typeof s.window === 'object' && clockSec(s.window.from) >= clockSec(s.window.to))
      ctx.addIssue({ code: 'custom', path: ['window'], message: 'window: "from" must be before "to" (same day)' })
    if (s.window && s.kind !== 'interval' && s.kind !== 'cron')
      ctx.addIssue({ code: 'custom', path: ['window'], message: 'window limits interval and cron schedules only (the others run at their own times)' })
    if (s.window && s.kind === 'interval' && (s.every ?? 0) >= 86_400)
      ctx.addIssue({ code: 'custom', path: ['window'], message: 'a window needs an interval under a day — for a gap of days use daily / weekly times' })
    if (s.start && s.end && s.start.replace('T', ' ') >= s.end.replace('T', ' '))
      ctx.addIssue({ code: 'custom', path: ['end'], message: 'end must be after start' })
  })
export type Schedule = z.infer<typeof ScheduleSchema>
export type ScheduleInput = z.input<typeof ScheduleSchema>

/** Seconds since midnight of "HH:MM[:SS]". */
export function clockSec(t: string): number {
  const m = CLOCK_RE.exec(t.trim())
  return m ? Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3] ?? 0) : 0
}
const secClock = (s: number, withSec = s % 60 !== 0) =>
  `${String(Math.floor(s / 3600)).padStart(2, '0')}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}${withSec ? `:${String(s % 60).padStart(2, '0')}` : ''}`

/** "9:5" / "09:05" / "0905" → "09:05"; "9:05:30" → "09:05:30"; null when it isn't a time. */
export function normClock(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const s = raw.trim()
  const m = /^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/.exec(s) ?? /^(\d{2})(\d{2})$/.exec(s)
  if (!m) return null
  const [h, mi, se] = [Number(m[1]), Number(m[2]), m[3] != null ? Number(m[3]) : null]
  if (h > 23 || mi > 59 || (se != null && se > 59)) return null
  return `${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}${se ? `:${String(se).padStart(2, '0')}` : ''}`
}

/** "2026-09-29T09:25" / "2026-09-29 9:25:30" → "2026-09-29 09:25[:30]"; null when it isn't one. */
export function normLocal(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}:\d{1,2}(?::\d{1,2})?))?$/.exec(raw.trim().replace(/Z$/, ''))
  if (!m) return null
  const clock = normClock(m[4] ?? '00:00')
  if (!clock) return null
  const date = `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`
  if (!validDate(date)) return null
  return `${date} ${clock}`
}

/**
 * A duration: seconds as a number, or text like "45s", "5m", "1h30m", "2d", "1w", "90" (seconds).
 * Returns whole seconds, or null.
 */
export function parseDuration(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) && raw > 0 ? Math.round(raw) : null
  if (typeof raw !== 'string') return null
  const s = raw.trim().toLowerCase().replace(/\s+/g, '')
  if (/^\d+(\.\d+)?$/.test(s)) return Math.round(Number(s)) || null
  const unit: Record<string, number> = { s: 1, sec: 1, secs: 1, second: 1, seconds: 1, m: 60, min: 60, mins: 60, minute: 60, minutes: 60, h: 3600, hr: 3600, hrs: 3600, hour: 3600, hours: 3600, d: 86_400, day: 86_400, days: 86_400, w: 604_800, wk: 604_800, week: 604_800, weeks: 604_800 }
  let total = 0
  let rest = s
  while (rest) {
    const m = /^(\d+(?:\.\d+)?)([a-z]+)/.exec(rest)
    if (!m || unit[m[2]] == null) return null
    total += Number(m[1]) * unit[m[2]]
    rest = rest.slice(m[0].length)
  }
  return total > 0 ? Math.round(total) : null
}

/** 90 → "90 seconds", 300 → "5 minutes", 5400 → "90 minutes", 7200 → "2 hours", 172800 → "2 days". */
export function durationText(sec: number): string {
  const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`
  if (sec % 604_800 === 0) return plural(sec / 604_800, 'week')
  if (sec % 86_400 === 0) return plural(sec / 86_400, 'day')
  if (sec % 3600 === 0) return plural(sec / 3600, 'hour')
  if (sec % 60 === 0) return plural(sec / 60, 'minute')
  return plural(sec, 'second')
}

/* ── dates + time zones ─────────────────────────────────────────────────── */

const pad2 = (n: number) => String(n).padStart(2, '0')
const isoOf = (y: number, m: number, d: number) => `${y}-${pad2(m)}-${pad2(d)}`
export const addDays = (date: string, n: number) => new Date(Date.parse(`${date}T12:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10)
/** 0 = Sunday. */
export const weekdayOf = (date: string) => new Date(`${date}T12:00:00Z`).getUTCDay()
export const daysInMonth = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate()
function validDate(date: string): boolean {
  const [y, m, d] = date.split('-').map(Number)
  return m >= 1 && m <= 12 && d >= 1 && d <= daysInMonth(y, m)
}

const fmtCache = new Map<string, Intl.DateTimeFormat>()
function fmtFor(tz: string): Intl.DateTimeFormat {
  let f = fmtCache.get(tz)
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      weekday: 'short',
    })
    fmtCache.set(tz, f)
  }
  return f
}

export function validTz(tz: string): boolean {
  try {
    fmtFor(tz)
    return true
  } catch {
    return false
  }
}

export interface ZonedParts {
  date: string
  y: number
  m: number
  d: number
  hh: number
  mm: number
  ss: number
  /** Seconds since local midnight. */
  sec: number
  /** 0 = Sunday. */
  wd: number
}
const WD: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }

/** The wall clock in `tz` at an instant. */
export function zonedParts(ms: number, tz: string): ZonedParts {
  const o: Record<string, string> = {}
  for (const p of fmtFor(tz).formatToParts(new Date(ms))) o[p.type] = p.value
  const y = Number(o.year)
  const m = Number(o.month)
  const d = Number(o.day)
  const hh = Number(o.hour) % 24
  const mm = Number(o.minute)
  const ss = Number(o.second)
  return { date: isoOf(y, m, d), y, m, d, hh, mm, ss, sec: hh * 3600 + mm * 60 + ss, wd: WD[o.weekday] ?? 0 }
}

const wallMs = (ms: number, tz: string) => {
  const p = zonedParts(ms, tz)
  return Date.UTC(p.y, p.m - 1, p.d, p.hh, p.mm, p.ss)
}

/**
 * The instant a wall-clock time happens in `tz` (seconds may overflow the day: 25:00 = 01:00 the
 * next day). An hour the clocks skip (spring forward) runs when they jump past it (02:30 → 03:30);
 * an hour that happens twice (fall back) runs the first time.
 */
export function zonedToEpoch(tz: string, date: string, sec = 0): number {
  const [y, m, d] = date.split('-').map(Number)
  const W = Date.UTC(y, m - 1, d, 0, 0, 0) + sec * 1000
  const off = (t: number) => wallMs(t, tz) - t
  const a = W - off(W - 43_200_000)
  const b = W - off(W + 43_200_000)
  if (a === b) return a
  const ok = [a, b].filter((t) => wallMs(t, tz) === W)
  return ok.length ? Math.min(...ok) : a
}

/** A local "YYYY-MM-DD HH:MM[:SS]" in `tz` → epoch ms (NaN when malformed). */
export function localToEpoch(local: string, tz: string): number {
  const m = LOCAL_RE.exec(local.trim())
  if (!m) return NaN
  return zonedToEpoch(tz, `${m[1]}-${m[2]}-${m[3]}`, Number(m[4]) * 3600 + Number(m[5]) * 60 + Number(m[6] ?? 0))
}

/** An instant as local "YYYY-MM-DD HH:MM:SS" in `tz`. */
export function epochToLocal(ms: number, tz: string): string {
  const p = zonedParts(ms, tz)
  return `${p.date} ${pad2(p.hh)}:${pad2(p.mm)}:${pad2(p.ss)}`
}

/** "ET", "UTC" or the zone's city. */
export function tzShort(tz: string): string {
  if (tz === ET) return 'ET'
  if (tz === 'UTC' || tz === 'Etc/UTC') return 'UTC'
  return tz.split('/').pop()!.replace(/_/g, ' ')
}

/* ── the NYSE calendar ───────────────────────────────────────────────────── */

function easter(y: number): [number, number] {
  const a = y % 19
  const b = Math.floor(y / 100)
  const c = y % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const n = h + l - 7 * m + 114
  return [Math.floor(n / 31), (n % 31) + 1]
}

/** The date of the `n`th (1–5, or -1 = last) `weekday` of a month; null when the month has no such day. */
export function nthWeekdayOf(y: number, m: number, n: number, weekday: number): string | null {
  if (n === -1) {
    const last = daysInMonth(y, m)
    const wd = weekdayOf(isoOf(y, m, last))
    return isoOf(y, m, last - ((wd - weekday + 7) % 7))
  }
  const first = weekdayOf(isoOf(y, m, 1))
  const day = 1 + ((weekday - first + 7) % 7) + (n - 1) * 7
  return day <= daysInMonth(y, m) ? isoOf(y, m, day) : null
}

interface NyseYear {
  closed: Map<string, string>
  early: Map<string, string>
}
const nyseCache = new Map<number, NyseYear>()

/** NYSE full holidays and 1 p.m. early closes of a year (the exchange's standing rules). */
export function nyseYear(y: number): NyseYear {
  const hit = nyseCache.get(y)
  if (hit) return hit
  const closed = new Map<string, string>()
  const early = new Map<string, string>()
  // A holiday on Saturday is observed on Friday, on Sunday on Monday — except New Year's Day on a
  // Saturday, which is not observed at all (Friday closes a year).
  const observed = (date: string, name: string, saturdayToFriday = true) => {
    const wd = weekdayOf(date)
    if (wd === 6) {
      if (saturdayToFriday) closed.set(addDays(date, -1), `${name} (observed)`)
    } else if (wd === 0) closed.set(addDays(date, 1), `${name} (observed)`)
    else closed.set(date, name)
  }
  observed(isoOf(y, 1, 1), "New Year's Day", false)
  closed.set(nthWeekdayOf(y, 1, 3, 1)!, 'Martin Luther King Jr. Day')
  closed.set(nthWeekdayOf(y, 2, 3, 1)!, "Washington's Birthday")
  const [em, ed] = easter(y)
  closed.set(addDays(isoOf(y, em, ed), -2), 'Good Friday')
  closed.set(nthWeekdayOf(y, 5, -1, 1)!, 'Memorial Day')
  if (y >= 2022) observed(isoOf(y, 6, 19), 'Juneteenth')
  observed(isoOf(y, 7, 4), 'Independence Day')
  closed.set(nthWeekdayOf(y, 9, 1, 1)!, 'Labor Day')
  const thanksgiving = nthWeekdayOf(y, 11, 4, 4)!
  closed.set(thanksgiving, 'Thanksgiving Day')
  observed(isoOf(y, 12, 25), 'Christmas Day')
  // Early closes (13:00 ET): July 3 when the 4th is Tue–Fri, the day after Thanksgiving, and
  // Christmas Eve on Mon–Thu.
  const july4 = weekdayOf(isoOf(y, 7, 4))
  if (july4 >= 2 && july4 <= 5) early.set(isoOf(y, 7, 3), 'Independence Day eve')
  early.set(addDays(thanksgiving, 1), 'Day after Thanksgiving')
  const xmasEve = weekdayOf(isoOf(y, 12, 24))
  if (xmasEve >= 1 && xmasEve <= 4) early.set(isoOf(y, 12, 24), 'Christmas Eve')
  const out = { closed, early }
  nyseCache.set(y, out)
  return out
}

/** Is this US Eastern date an NYSE session? */
export function isTradingDay(date: string): boolean {
  const wd = weekdayOf(date)
  return wd !== 0 && wd !== 6 && !nyseYear(Number(date.slice(0, 4))).closed.has(date)
}

/** Why the NYSE is shut on a weekday (a holiday's name), else null. */
export function nyseHoliday(date: string): string | null {
  return nyseYear(Number(date.slice(0, 4))).closed.get(date) ?? null
}

export interface MarketHours {
  /** Seconds after midnight US Eastern. */
  premarket: number
  open: number
  close: number
  afterhours: number
  early: boolean
}
/** An NYSE session's hours (early-close days end at 13:00, their after-hours at 17:00); null when closed. */
export function marketHours(date: string): MarketHours | null {
  if (!isTradingDay(date)) return null
  const early = nyseYear(Number(date.slice(0, 4))).early.has(date)
  return { premarket: 4 * 3600, open: 9.5 * 3600, close: (early ? 13 : 16) * 3600, afterhours: (early ? 17 : 20) * 3600, early }
}

/** The next NYSE session date on or after `date`. */
export function nextTradingDay(date: string, inclusive = true): string {
  let d = inclusive ? date : addDays(date, 1)
  for (let i = 0; i < 15 && !isTradingDay(d); i++) d = addDays(d, 1)
  return d
}

/* ── cron ────────────────────────────────────────────────────────────────── */

export interface CronSpec {
  sec: number[]
  min: number[]
  hour: number[]
  dom: Set<number>
  mon: Set<number>
  dow: Set<number>
  domAny: boolean
  dowAny: boolean
  /** "L" in the day field: the month's last day. */
  lastDom: boolean
}
const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
const DOWS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']

function cronField(text: string, lo: number, hi: number, names: string[] | null, label: string): number[] | string {
  const out = new Set<number>()
  const val = (v: string): number | null => {
    const up = v.toUpperCase()
    if (names) {
      const i = names.indexOf(up)
      if (i >= 0) return i + (label === 'month' ? 1 : 0)
    }
    if (!/^\d+$/.test(v)) return null
    const n = Number(v)
    return label === 'weekday' && n === 7 ? 0 : n
  }
  for (const part of text.split(',')) {
    const [range, stepText] = part.split('/')
    const step = stepText == null ? 1 : Number(stepText)
    if (!Number.isInteger(step) || step < 1) return `cron ${label}: bad step in "${part}"`
    let a: number
    let b: number
    if (range === '*' || range === '?') {
      a = lo
      b = hi
    } else if (range.includes('-')) {
      const [x, y] = range.split('-')
      const va = val(x)
      const vb = val(y)
      if (va == null || vb == null) return `cron ${label}: bad range "${range}"`
      a = va
      b = label === 'weekday' && y === '7' ? 6 : vb
      if (label === 'weekday' && y === '7' && va === 0) b = 6
    } else {
      const v = val(range)
      if (v == null) return `cron ${label}: bad value "${range}"`
      a = v
      b = stepText != null ? hi : v
    }
    if (a < lo || b > hi || a > b) return `cron ${label}: "${part}" is outside ${lo}–${hi}`
    for (let i = a; i <= b; i += step) out.add(i)
  }
  return [...out].sort((x, y) => x - y)
}

/** "sec min hour day month weekday" (or 5 fields without seconds) → a spec, or an error text. */
export function parseCron(expr: string): CronSpec | string {
  const f = expr.trim().split(/\s+/)
  if (f.length !== 5 && f.length !== 6) return 'cron has 6 fields "sec min hour day month weekday" (or 5 without seconds)'
  const [s, mi, h, dom, mon, dow] = f.length === 6 ? f : ['0', ...f]
  const sec = cronField(s, 0, 59, null, 'second')
  const min = cronField(mi, 0, 59, null, 'minute')
  const hour = cronField(h, 0, 23, null, 'hour')
  const lastDom = dom.toUpperCase() === 'L'
  const domL = lastDom ? [] : cronField(dom, 1, 31, null, 'day')
  const monL = cronField(mon, 1, 12, MONTHS, 'month')
  const dowL = cronField(dow, 0, 6, DOWS, 'weekday')
  for (const x of [sec, min, hour, domL, monL, dowL]) if (typeof x === 'string') return x
  return {
    sec: sec as number[],
    min: min as number[],
    hour: hour as number[],
    dom: new Set(domL as number[]),
    mon: new Set(monL as number[]),
    dow: new Set(dowL as number[]),
    domAny: !lastDom && (dom === '*' || dom === '?'),
    dowAny: dow === '*' || dow === '?',
    lastDom,
  }
}

function cronDayMatch(c: CronSpec, date: string): boolean {
  const [y, m, d] = date.split('-').map(Number)
  if (!c.mon.has(m)) return false
  const domHit = c.lastDom ? d === daysInMonth(y, m) : c.dom.has(d)
  const dowHit = c.dow.has(weekdayOf(date))
  // Classic cron: when both the day and the weekday are restricted, either one matching is enough.
  if (c.domAny && c.dowAny) return true
  if (c.domAny) return dowHit
  if (c.dowAny) return domHit
  return domHit || dowHit
}

/* ── next runs ───────────────────────────────────────────────────────────── */

export interface ScheduleContext {
  /** When the job was created — an interval without `start` counts from here. */
  created: number
  /** Runs so far (with `maxRuns`, none after the last). */
  runs?: number
}

const dayOk = (date: string, days: DayFilter) =>
  days === 'all' ? true : days === 'weekdays' ? weekdayOf(date) % 6 !== 0 : isTradingDay(date)

/** A monthly / yearly date that misses the day filter moves to the next allowed day in its month, else the previous. */
function adjustDay(date: string, days: DayFilter, preferEarlier: boolean): string | null {
  if (dayOk(date, days)) return date
  const month = date.slice(0, 7)
  const step = (dir: number) => {
    let d = date
    for (let i = 0; i < 10; i++) {
      d = addDays(d, dir)
      if (d.slice(0, 7) !== month) return null
      if (dayOk(d, days)) return d
    }
    return null
  }
  return preferEarlier ? (step(-1) ?? step(1)) : (step(1) ?? step(-1))
}

function monthRunDates(s: Schedule, y: number, m: number): Set<string> {
  const out = new Set<string>()
  if (s.months && !s.months.includes(m)) return out
  const last = daysInMonth(y, m)
  const add = (d: string | null, preferEarlier: boolean) => {
    const a = d ? adjustDay(d, s.days, preferEarlier) : null
    if (a) out.add(a)
  }
  if (s.kind === 'monthly') {
    for (const md of s.monthDays ?? []) add(isoOf(y, m, md === -1 ? last : Math.min(md, last)), md === -1 || md >= last)
    if (s.nth) add(nthWeekdayOf(y, m, s.nth.n, s.nth.weekday), s.nth.n === -1)
  } else if (s.kind === 'yearly') {
    for (const md of s.dates ?? []) {
      const [mm, dd] = md.split('-').map(Number)
      if (mm === m) add(isoOf(y, m, Math.min(dd, last)), dd >= last)
    }
  }
  return out
}

/** A window's bounds on a date (epoch ms), or null when the window is shut that day. */
function windowBounds(w: ScheduleWindow, date: string, tz: string): [number, number] | null {
  if (w === 'session' || w === 'extended') {
    const h = marketHours(date)
    if (!h) return null
    return w === 'session' ? [zonedToEpoch(ET, date, h.open), zonedToEpoch(ET, date, h.close)] : [zonedToEpoch(ET, date, h.premarket), zonedToEpoch(ET, date, h.afterhours)]
  }
  return [zonedToEpoch(tz, date, clockSec(w.from)), zonedToEpoch(tz, date, clockSec(w.to))]
}
const windowTz = (w: ScheduleWindow, tz: string) => (w === 'session' || w === 'extended' ? ET : tz)

function inWindow(w: ScheduleWindow, t: number, tz: string): boolean {
  const wtz = windowTz(w, tz)
  const b = windowBounds(w, zonedParts(t, wtz).date, wtz)
  return !!b && t >= b[0] && t < b[1]
}

const ceilTo = (from: number, base: number, step: number) => (from <= base ? base : base + Math.ceil((from - base) / step) * step)

function nextInterval(s: Schedule, from: number, end: number, ctx: ScheduleContext): number | null {
  const S = s.every! * 1000
  const hasStart = !!s.start
  const anchor = hasStart ? localToEpoch(s.start!, s.tz) : Math.floor(ctx.created / 1000) * 1000
  const first = hasStart ? anchor : anchor + S
  if (s.window) {
    // Runs line up with the window's start each allowed day: every 15 min from 09:30.
    const wtz = windowTz(s.window, s.tz)
    let date = zonedParts(from, wtz).date
    for (let i = 0; i < 800; i++, date = addDays(date, 1)) {
      if (!dayOk(date, s.days)) continue
      const b = windowBounds(s.window, date, wtz)
      if (!b) continue
      if (b[0] > end) return null
      if (b[1] <= from) continue
      const t = ceilTo(from, b[0], S)
      if (t < b[1]) return t <= end ? t : null
    }
    return null
  }
  let t = ceilTo(from, first, S)
  if (s.days === 'all') return t <= end ? t : null
  for (let i = 0; i < 2000; i++) {
    if (t > end) return null
    const date = zonedParts(t, s.tz).date
    if (dayOk(date, s.days)) return t
    // Skip to the next day and line up again.
    t = ceilTo(zonedToEpoch(s.tz, addDays(date, 1), 0), first, S)
  }
  return null
}

/** The first cron second on `date` that is ≥ from (and inside the window). */
function cronInDay(c: CronSpec, s: Schedule, date: string, from: number): number | null {
  const midnight = zonedToEpoch(s.tz, date, 0)
  // Plain arithmetic within a day, unless the clocks change that day.
  const steady = zonedToEpoch(s.tz, addDays(date, 1), 0) - midnight === DAY_MS
  const at = (sec: number) => (steady ? midnight + sec * 1000 : zonedToEpoch(s.tz, date, sec))
  // A window in the schedule's own zone bounds the search directly; a US-session window seen
  // from another zone is checked per candidate.
  let lo = from
  let hi = Infinity
  const direct = s.window != null && windowTz(s.window, s.tz) === s.tz
  if (s.window && direct) {
    const b = windowBounds(s.window, date, s.tz)
    if (!b) return null
    lo = Math.max(from, b[0])
    hi = b[1]
  }
  for (const h of c.hour) {
    if (at(h * 3600 + 3599) < lo) continue
    if (at(h * 3600) >= hi) break
    for (const mi of c.min) {
      if (at(h * 3600 + mi * 60 + 59) < lo) continue
      for (const se of c.sec) {
        const t = at(h * 3600 + mi * 60 + se)
        if (t < lo) continue
        if (t >= hi) return null
        if (s.window && !direct && !inWindow(s.window, t, s.tz)) continue
        return t
      }
    }
  }
  return null
}

/**
 * The first run strictly after `after` (epoch ms), or null when there is none (a past moment, the
 * end reached, the runs used up). Whole seconds.
 */
export function nextRun(s: Schedule, after: number, ctx: ScheduleContext): number | null {
  if (s.maxRuns != null && (ctx.runs ?? 0) >= s.maxRuns) return null
  const startMs = s.start ? localToEpoch(s.start, s.tz) : -Infinity
  const end = s.end ? localToEpoch(s.end, s.tz) : Infinity
  const from = Math.max(Math.floor(after / 1000) * 1000 + 1000, startMs)
  if (from > end) return null
  switch (s.kind) {
    case 'once': {
      const ts = (s.at ?? []).map((a) => localToEpoch(a, s.tz)).filter((t) => t >= from && t <= end)
      return ts.length ? Math.min(...ts) : null
    }
    case 'interval':
      return nextInterval(s, from, end, ctx)
    case 'market': {
      let date = zonedParts(from, ET).date
      for (let i = 0; i < 800; i++, date = addDays(date, 1)) {
        const h = marketHours(date)
        if (!h) continue
        const t = zonedToEpoch(ET, date, h[s.event!] + (s.offsetMin ?? 0) * 60)
        if (t > end) return null
        if (t >= from) return t
      }
      return null
    }
    default: {
      const cron = s.kind === 'cron' ? (parseCron(s.cron!) as CronSpec) : null
      const times = (s.times ?? []).map(clockSec).sort((a, b) => a - b)
      const months = new Map<string, Set<string>>()
      let date = zonedParts(from, s.tz).date
      const horizon = s.kind === 'yearly' ? 366 * 9 : s.kind === 'cron' ? 366 * 6 : 800
      for (let i = 0; i < horizon; i++, date = addDays(date, 1)) {
        if (end !== Infinity && zonedToEpoch(s.tz, date, 0) > end) return null
        let match: boolean
        if (s.kind === 'daily') match = dayOk(date, s.days)
        else if (s.kind === 'weekly') match = s.weekdays!.includes(weekdayOf(date)) && dayOk(date, s.days)
        else if (s.kind === 'cron') match = cronDayMatch(cron!, date) && dayOk(date, s.days)
        else {
          const key = date.slice(0, 7)
          let set = months.get(key)
          if (!set) {
            set = monthRunDates(s, Number(date.slice(0, 4)), Number(date.slice(5, 7)))
            months.set(key, set)
          }
          match = set.has(date)
        }
        if (!match) continue
        if (cron) {
          const t = cronInDay(cron, s, date, from)
          if (t != null) return t <= end ? t : null
          continue
        }
        for (const sec of times) {
          const t = zonedToEpoch(s.tz, date, sec)
          if (t >= from) return t <= end ? t : null
        }
      }
      return null
    }
  }
}

/** The next `n` runs after `after`. */
export function nextRuns(s: Schedule, after: number, ctx: ScheduleContext, n: number): number[] {
  const out: number[] = []
  let t = after
  let runs = ctx.runs ?? 0
  for (let i = 0; i < n; i++) {
    const next = nextRun(s, t, { ...ctx, runs })
    if (next == null) break
    out.push(next)
    t = next
    runs++
  }
  return out
}

/**
 * About how many runs a day this schedule makes (an average over the next two weeks) — for the
 * AI-run budget. Intervals are worked out, not counted.
 */
export function runsPerDay(s: Schedule, now: number, ctx: ScheduleContext): number {
  const span = 14 * DAY_MS
  if (s.kind === 'interval' && s.every) {
    let covered = 0
    if (!s.window) {
      if (s.days === 'all') return 86_400 / s.every
      let date = zonedParts(now, s.tz).date
      for (let i = 0; i < 14; i++, date = addDays(date, 1)) if (dayOk(date, s.days)) covered += 86_400
    } else {
      const wtz = windowTz(s.window, s.tz)
      let date = zonedParts(now, wtz).date
      for (let i = 0; i < 14; i++, date = addDays(date, 1)) {
        if (!dayOk(date, s.days)) continue
        const b = windowBounds(s.window, date, wtz)
        if (b) covered += Math.ceil((b[1] - b[0]) / 1000)
      }
    }
    return covered / s.every / 14
  }
  let count = 0
  let t = now
  let runs = ctx.runs ?? 0
  while (count < 5000) {
    const next = nextRun(s, t, { ...ctx, runs })
    if (next == null || next > now + span) break
    count++
    runs++
    t = next
  }
  return count / 14
}

/* ── words ───────────────────────────────────────────────────────────────── */

const WEEKDAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const ordinal = (n: number) => (n === -1 ? 'last day' : `${n}${n % 10 === 1 && n !== 11 ? 'st' : n % 10 === 2 && n !== 12 ? 'nd' : n % 10 === 3 && n !== 13 ? 'rd' : 'th'}`)
const list = (xs: string[]) => (xs.length <= 1 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} and ${xs.at(-1)}`)
const clockText = (t: string) => secClock(clockSec(t))
/** "Tue 29 Sep 2026 09:25" (seconds when not :00). */
export function localText(local: string): string {
  const m = LOCAL_RE.exec(local.trim())
  if (!m) return local
  const date = `${m[1]}-${m[2]}-${m[3]}`
  const time = `${m[4]}:${m[5]}${m[6] && m[6] !== '00' ? `:${m[6]}` : ''}`
  return `${WEEKDAY_NAMES[weekdayOf(date)]} ${Number(m[3])} ${MONTH_NAMES[Number(m[2]) - 1]} ${m[1]} ${time}`
}

const EVENT_TEXT: Record<MarketEvent, string> = { premarket: 'pre-market start (04:00)', open: 'the open', close: 'the close', afterhours: 'the end of after-hours' }

/** The schedule in a short English line: "Every 5 minutes during the US session, trading days". */
export function describeSchedule(s: Schedule): string {
  const tz = tzShort(s.tz)
  const at = (s.times ?? []).map(clockText)
  const times = at.length ? ` at ${list(at)} ${tz}` : ''
  const daysText = s.days === 'trading' ? ' on trading days' : s.days === 'weekdays' ? ' on weekdays' : ''
  const windowText = !s.window
    ? ''
    : s.window === 'session'
      ? ' during the US session'
      : s.window === 'extended'
        ? ' during US pre/regular/after-hours'
        : ` between ${clockText(s.window.from)} and ${clockText(s.window.to)} ${tz}`
  let base: string
  switch (s.kind) {
    case 'once':
      base = s.at!.length === 1 ? `Once — ${localText(s.at![0])} ${tz}` : `${s.at!.length} times — ${s.at!.slice(0, 4).map(localText).join(', ')}${s.at!.length > 4 ? ', …' : ''} ${tz}`
      break
    case 'interval':
      base = `Every ${durationText(s.every!).replace(/^1 /, '')}${windowText}${daysText}`
      break
    case 'daily':
      base = `${s.days === 'trading' ? 'Every US trading day' : s.days === 'weekdays' ? 'Every weekday' : 'Every day'}${times}`
      break
    case 'weekly':
      base = `Every ${list(s.weekdays!.slice().sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7)).map((d) => WEEKDAY_NAMES[d]))}${times}${daysText}`
      break
    case 'monthly': {
      const which = [
        ...(s.monthDays ?? []).map((d) => (d === -1 ? 'the last day' : `the ${ordinal(d)}`)),
        ...(s.nth ? [`the ${s.nth.n === -1 ? 'last' : ordinal(s.nth.n)} ${WEEKDAY_NAMES[s.nth.weekday]}`] : []),
      ]
      const months = s.months ? ` in ${list(s.months.map((m) => MONTH_NAMES[m - 1]))}` : ''
      base = `${s.months?.length === 4 && [0, 1, 2].every((i) => s.months![i + 1] - s.months![i] === 3) ? 'Quarterly' : 'Monthly'} on ${list(which)}${months}${times}${daysText}`
      break
    }
    case 'yearly':
      base = `Every year on ${list(s.dates!.map((d) => `${MONTH_NAMES[Number(d.slice(0, 2)) - 1]} ${Number(d.slice(3))}`))}${times}${daysText}`
      break
    case 'market': {
      const off = s.offsetMin ?? 0
      const rel = off === 0 ? `at ${EVENT_TEXT[s.event!]}` : `${durationText(Math.abs(off) * 60)} ${off < 0 ? 'before' : 'after'} ${EVENT_TEXT[s.event!]}`
      base = `Every US trading day, ${rel}`
      break
    }
    case 'cron':
      base = `Cron "${s.cron}" ${tz}${windowText}${daysText}`
      break
  }
  const bounds = [
    s.start ? `from ${localText(s.start)}` : '',
    s.end ? `until ${localText(s.end)}` : '',
    s.maxRuns ? `at most ${s.maxRuns} run${s.maxRuns === 1 ? '' : 's'}` : '',
  ].filter(Boolean)
  return bounds.length && s.kind !== 'once' ? `${base} (${bounds.join(', ')})` : base
}

/* ── friendly input → a schedule ─────────────────────────────────────────── */

const WEEKDAY_WORDS: Record<string, number> = {
  sun: 0, sunday: 0, pazar: 0, mon: 1, monday: 1, pazartesi: 1, tue: 2, tuesday: 2, sali: 2, 'salı': 2, wed: 3, wednesday: 3,
  carsamba: 3, 'çarşamba': 3, thu: 4, thursday: 4, persembe: 4, 'perşembe': 4, fri: 5, friday: 5, cuma: 5, sat: 6, saturday: 6, cumartesi: 6,
}
const asArray = (v: unknown): unknown[] => (v == null ? [] : Array.isArray(v) ? v : [v])

/**
 * Loose input (the AI's tool arguments, the UI's form) → a schedule, or the reasons it isn't one.
 * Accepts: `every` as seconds or "5m"/"1h30m"; `in` ("90s", "2h") for a single moment from now;
 * one value or a list for times / at / weekdays (numbers or names) / monthDays / dates; "H:MM".
 */
export function scheduleFromInput(raw: unknown, now: number, defaultTz = DEFAULT_TZ): { ok: true; schedule: Schedule } | { ok: false; errors: string[] } {
  if (!raw || typeof raw !== 'object') return { ok: false, errors: ['schedule: an object with kind'] }
  const r = raw as Record<string, unknown>
  const tz = typeof r.tz === 'string' && r.tz.trim() ? r.tz.trim() : defaultTz
  const errors: string[] = []
  const out: Record<string, unknown> = { kind: r.kind, tz }
  if (r.in != null && (r.kind == null || r.kind === 'once')) {
    const sec = parseDuration(r.in)
    if (sec == null) errors.push(`in: "${String(r.in)}" is not a duration (e.g. "90s", "15m", "2h", "1d")`)
    else {
      out.kind = 'once'
      out.at = [epochToLocal(Math.ceil((now + sec * 1000) / 1000) * 1000, tz)]
    }
  } else if (r.at != null) {
    const at = asArray(r.at).map(normLocal)
    if (at.some((a) => a == null)) errors.push('at: moments are "YYYY-MM-DD HH:MM" or "YYYY-MM-DD HH:MM:SS"')
    else out.at = [...new Set(at as string[])].sort()
  }
  if (r.every != null) {
    const sec = parseDuration(r.every)
    if (sec == null) errors.push(`every: "${String(r.every)}" is not a duration (seconds, or "30s", "5m", "1h", "2d")`)
    else out.every = sec
  }
  if (r.times != null) {
    const ts = asArray(r.times).map(normClock)
    if (ts.some((t) => t == null)) errors.push('times: each is "HH:MM" or "HH:MM:SS"')
    else out.times = [...new Set(ts as string[])].sort((a, b) => clockSec(a) - clockSec(b))
  }
  if (r.weekdays != null) {
    const wds = asArray(r.weekdays).map((w) => (typeof w === 'number' ? (w === 7 ? 0 : w) : WEEKDAY_WORDS[String(w).trim().toLowerCase()]))
    if (wds.some((w) => w == null || !Number.isInteger(w) || w < 0 || w > 6)) errors.push('weekdays: 0 = Sun … 6 = Sat (or names)')
    else out.weekdays = [...new Set(wds as number[])].sort()
  }
  if (r.monthDays != null) out.monthDays = [...new Set(asArray(r.monthDays).map((d) => (d === 'last' || d === 'L' ? -1 : Number(d))))]
  if (r.nth != null && typeof r.nth === 'object') {
    const n = r.nth as Record<string, unknown>
    const wd = typeof n.weekday === 'number' ? n.weekday : WEEKDAY_WORDS[String(n.weekday).trim().toLowerCase()]
    out.nth = { n: n.n === 'last' ? -1 : Number(n.n), weekday: wd }
  }
  if (r.months != null) out.months = [...new Set(asArray(r.months).map(Number))].sort((a, b) => a - b)
  if (r.dates != null)
    out.dates = asArray(r.dates).map((d) => {
      const m = /^(\d{1,2})-(\d{1,2})$/.exec(String(d).trim())
      return m ? `${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}` : String(d)
    })
  if (r.event != null) out.event = r.event
  if (r.offsetMin != null) out.offsetMin = Math.round(Number(r.offsetMin))
  if (r.cron != null) out.cron = String(r.cron)
  if (r.days != null) out.days = r.days
  if (r.window != null) {
    if (r.window === 'session' || r.window === 'extended') out.window = r.window
    else if (typeof r.window === 'object') {
      const w = r.window as Record<string, unknown>
      const from = normClock(w.from)
      const to = normClock(w.to)
      if (!from || !to) errors.push('window: {from:"HH:MM", to:"HH:MM"} or "session" / "extended"')
      else out.window = { from, to }
    }
  }
  for (const k of ['start', 'end'] as const) {
    if (r[k] == null || r[k] === '') continue
    const v = normLocal(r[k])
    if (!v) errors.push(`${k}: "YYYY-MM-DD HH:MM[:SS]"`)
    else out[k] = v
  }
  if (r.maxRuns != null) out.maxRuns = Math.round(Number(r.maxRuns))
  if (errors.length) return { ok: false, errors }
  const parsed = ScheduleSchema.safeParse(out)
  if (!parsed.success) return { ok: false, errors: parsed.error.issues.map((i) => `${i.path.join('.') || 'schedule'}: ${i.message}`) }
  return { ok: true, schedule: parsed.data }
}
